/**
 * @voltxampere/engine — ERC rule: short-circuit (Sprint 56)
 *
 * Two ideal voltage sources connected with no resistance between
 * them — physically impossible (the sources would need to deliver
 * infinite current to enforce both target voltages). The MNA solver
 * detects this as a singular matrix at LU time; this ERC rule
 * catches it pre-solve so the UI surfaces a clean hint instead of
 * the cryptic "zero pivot at column N" error.
 *
 * Detection heuristic (S56a MVP — topological):
 *   - Two `voltage-source` (or `ac-source`/`pulse-source`/etc.)
 *     components whose `pos` + `neg` terminals share BOTH net IDs.
 *   - Same source paralleled with a 0-Ω resistor (resistance ≤ 0).
 *
 * Sprint 57+ may add a graph-walk for indirect shorts (V-source +
 * R chain ending at the other rail with all R = 0). MVP catches
 * direct-parallel + 0-R cases which are the common authoring bug.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
const SOURCE_TYPES = new Set([
    "voltage-source",
    "ac-source",
    "pulse-source",
    "pwl-source",
    "b-source",
]);
export const shortCircuitRule = {
    id: "short-circuit",
    defaultSeverity: "error",
    requiresDCResult: false,
    check(circuit) {
        const violations = [];
        // Helper: get sorted (pos, neg) pair for stable comparison.
        function netPair(comp) {
            const pos = comp.terminals.find((t) => t.terminalId === "pos")?.netId;
            const neg = comp.terminals.find((t) => t.terminalId === "neg")?.netId;
            if (pos === undefined || neg === undefined)
                return null;
            return { pos, neg };
        }
        // 1. Two voltage sources sharing exact pos/neg net pair.
        const sources = circuit.components.filter((c) => SOURCE_TYPES.has(c.type));
        for (let i = 0; i < sources.length; i++) {
            const a = sources[i];
            const pairA = a ? netPair(a) : null;
            if (!a || !pairA)
                continue;
            for (let j = i + 1; j < sources.length; j++) {
                const b = sources[j];
                const pairB = b ? netPair(b) : null;
                if (!b || !pairB)
                    continue;
                if (pairA.pos === pairB.pos && pairA.neg === pairB.neg) {
                    violations.push({
                        ruleId: "short-circuit",
                        severity: "error",
                        i18nKey: "erc.short-circuit.message",
                        suggestionKey: "erc.short-circuit.suggestion",
                        meta: {
                            kind: "parallel-sources",
                            componentIdA: a.id,
                            componentIdB: b.id,
                            posNetId: pairA.pos,
                            negNetId: pairA.neg,
                        },
                    });
                }
            }
        }
        // 2. Source paralleled with a 0-Ω resistor.
        const resistors = circuit.components.filter((c) => c.type === "resistor");
        for (const src of sources) {
            const srcPair = netPair(src);
            if (!srcPair)
                continue;
            for (const r of resistors) {
                const rP1 = r.terminals.find((t) => t.terminalId === "p1")?.netId;
                const rP2 = r.terminals.find((t) => t.terminalId === "p2")?.netId;
                if (rP1 === undefined || rP2 === undefined)
                    continue;
                // Both endpoints of R match both endpoints of source (either order).
                const matches = (rP1 === srcPair.pos && rP2 === srcPair.neg) ||
                    (rP1 === srcPair.neg && rP2 === srcPair.pos);
                if (!matches)
                    continue;
                const rawR = r.params.resistance;
                const resistance = typeof rawR === "number" ? rawR : Number.NaN;
                if (Number.isFinite(resistance) && resistance <= 0) {
                    violations.push({
                        ruleId: "short-circuit",
                        severity: "error",
                        i18nKey: "erc.short-circuit.message",
                        suggestionKey: "erc.short-circuit.suggestion",
                        meta: {
                            kind: "zero-resistor-parallel-source",
                            sourceId: src.id,
                            resistorId: r.id,
                            resistance,
                        },
                    });
                }
            }
        }
        return violations;
    },
};
//# sourceMappingURL=short-circuit.js.map